import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';

const ParticleCloud = () => {
  const pointsRef1 = useRef<THREE.Points>(null);
  const pointsRef2 = useRef<THREE.Points>(null);
  const pointsRef3 = useRef<THREE.Points>(null);
  
  const pulseTimerRef = useRef<number>(0);

  const count = 4000;

  // Rejection sampling for positions within a sphere of radius 4
  const originalPositions = useMemo(() => {
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      let x = 0, y = 0, z = 0;
      let d = 999;
      while (d > 4) {
        x = (Math.random() - 0.5) * 8;
        y = (Math.random() - 0.5) * 8;
        z = (Math.random() - 0.5) * 8;
        d = Math.sqrt(x * x + y * y + z * z);
      }
      arr[i * 3] = x;
      arr[i * 3 + 1] = y;
      arr[i * 3 + 2] = z;
    }
    return arr;
  }, []);

  // Compute colors based on distance from core
  const colors = useMemo(() => {
    const cols = new Float32Array(count * 3);
    const colorPurple = new THREE.Color('#5B21B6');
    const colorBlue = new THREE.Color('#3B82F6');
    const colorOrange = new THREE.Color('#F97316');

    for (let i = 0; i < count; i++) {
      const x = originalPositions[i * 3];
      const y = originalPositions[i * 3 + 1];
      const z = originalPositions[i * 3 + 2];
      const dist = Math.sqrt(x * x + y * y + z * z);

      const finalColor = new THREE.Color();
      if (dist > 2) {
        // Outer zone: blue -> purple
        const t = Math.min((dist - 2) / 2, 1);
        finalColor.copy(colorBlue).lerp(colorPurple, t);
      } else if (dist < 1) {
        // Inner core: orange -> blue
        const t = dist; // 0 to 1
        finalColor.copy(colorOrange).lerp(colorBlue, t);
      } else {
        // Middle zone
        finalColor.copy(colorBlue);
      }

      cols[i * 3] = finalColor.r;
      cols[i * 3 + 1] = finalColor.g;
      cols[i * 3 + 2] = finalColor.b;
    }
    return cols;
  }, [originalPositions]);

  // Separate buffer arrays for the 3 groups to achieve varying point sizes
  const groups = useMemo(() => {
    // 3 subsets: G1 (sizes 0.03), G2 (sizes 0.05), G3 (sizes 0.08)
    const g1Idx: number[] = [];
    const g2Idx: number[] = [];
    const g3Idx: number[] = [];

    for (let i = 0; i < count; i++) {
      const rand = Math.random();
      if (rand < 0.35) {
        g1Idx.push(i);
      } else if (rand < 0.75) {
        g2Idx.push(i);
      } else {
        g3Idx.push(i);
      }
    }

    const buildGroup = (indices: number[]) => {
      const pos = new Float32Array(indices.length * 3);
      const cols = new Float32Array(indices.length * 3);
      indices.forEach((origIdx, gIdx) => {
        pos[gIdx * 3] = originalPositions[origIdx * 3];
        pos[gIdx * 3 + 1] = originalPositions[origIdx * 3 + 1];
        pos[gIdx * 3 + 2] = originalPositions[origIdx * 3 + 2];

        cols[gIdx * 3] = colors[origIdx * 3];
        cols[gIdx * 3 + 1] = colors[origIdx * 3 + 1];
        cols[gIdx * 3 + 2] = colors[origIdx * 3 + 2];
      });
      return { indices, pos, cols };
    };

    return {
      g1: buildGroup(g1Idx),
      g2: buildGroup(g2Idx),
      g3: buildGroup(g3Idx)
    };
  }, [originalPositions, colors]);

  useFrame((state, delta) => {
    // Slowly rotate point meshes
    if (pointsRef1.current) pointsRef1.current.rotation.y += 0.002;
    if (pointsRef2.current) pointsRef2.current.rotation.y += 0.002;
    if (pointsRef3.current) pointsRef3.current.rotation.y += 0.002;

    pulseTimerRef.current += delta;
    const pulseCycle = pulseTimerRef.current % 9; // 9s collapse loop
    const cycleCount = Math.floor(pulseCycle / 3); // 0, 1, 2 sub-cycles
    const cycleProgress = (pulseCycle % 3) / 3;

    // Drifting inward by up to 1% per 3s sub-cycle (cumulative)
    const currentDrift = (cycleCount * 0.01) + (cycleProgress * 0.01);

    const updateDrift = (ref: React.RefObject<THREE.Points>, groupData: any) => {
      const geom = ref.current?.geometry;
      if (geom) {
        const posAttr = geom.getAttribute('position') as THREE.BufferAttribute;
        if (posAttr) {
          groupData.indices.forEach((origIdx: number, gIdx: number) => {
            const ox = originalPositions[origIdx * 3];
            const oy = originalPositions[origIdx * 3 + 1];
            const oz = originalPositions[origIdx * 3 + 2];
            const dist = Math.sqrt(ox * ox + oy * oy + oz * oz);

            if (dist < 2.0) {
              // Collapse central core
              posAttr.setXYZ(gIdx, ox * (1 - currentDrift), oy * (1 - currentDrift), oz * (1 - currentDrift));
            } else {
              posAttr.setXYZ(gIdx, ox, oy, oz);
            }
          });
          posAttr.needsUpdate = true;
        }
      }
    };

    updateDrift(pointsRef1, groups.g1);
    updateDrift(pointsRef2, groups.g2);
    updateDrift(pointsRef3, groups.g3);
  });

  return (
    <>
      <points ref={pointsRef1}>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" args={[groups.g1.pos, 3]} />
          <bufferAttribute attach="attributes-color" args={[groups.g1.cols, 3]} />
        </bufferGeometry>
        <pointsMaterial vertexColors size={0.03} sizeAttenuation transparent opacity={0.65} />
      </points>

      <points ref={pointsRef2}>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" args={[groups.g2.pos, 3]} />
          <bufferAttribute attach="attributes-color" args={[groups.g2.cols, 3]} />
        </bufferGeometry>
        <pointsMaterial vertexColors size={0.055} sizeAttenuation transparent opacity={0.7} />
      </points>

      <points ref={pointsRef3}>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" args={[groups.g3.pos, 3]} />
          <bufferAttribute attach="attributes-color" args={[groups.g3.cols, 3]} />
        </bufferGeometry>
        <pointsMaterial vertexColors size={0.08} sizeAttenuation transparent opacity={0.75} />
      </points>
    </>
  );
};

const ProtoStar = () => {
  const starRef = useRef<THREE.Mesh>(null);

  useFrame((state) => {
    if (starRef.current) {
      const time = state.clock.getElapsedTime();
      // Pulsate scale between 0.8 and 1.2
      const scale = 1.0 + Math.sin(time * 2.5) * 0.2;
      starRef.current.scale.set(scale, scale, scale);
    }
  });

  return (
    <mesh ref={starRef}>
      <sphereGeometry args={[0.3, 32, 32]} />
      <meshStandardMaterial
        color="#F97316"
        emissive="#F97316"
        emissiveIntensity={2}
        roughness={0.15}
      />
    </mesh>
  );
};

export function NebulaMolecularCloud() {
  return (
    <div className="relative w-full h-[340px] rounded-xl overflow-hidden bg-[#0F172A] border border-white/10 select-none">
      <Canvas camera={{ position: [0, 0, 8], fof: 45 } as any}>
        <color attach="background" args={['#0F172A']} />
        <fog attach="fog" args={['#0F172A', 8, 25]} />
        
        <ambientLight intensity={0.3} />
        <pointLight position={[0, 0, 0]} color="#F97316" intensity={2} decay={1} distance={15} />

        <ParticleCloud />
        <ProtoStar />

        <OrbitControls enableZoom={false} autoRotate autoRotateSpeed={0.3} />
      </Canvas>

      {/* Absolute HTML Overlays */}
      <div className="absolute top-3 right-3 bg-[#0b0f19]/80 backdrop-blur-sm p-2.5 rounded-lg border border-white/10 flex flex-col gap-1.5 pointer-events-none select-none text-[9px] font-bold text-white/70">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#5B21B6]" />
          <span>Outer: ~10K (cold)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#3B82F6]" />
          <span>Middle: ~100K</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#F97316]" />
          <span>Core: ~1M+ K (collapsing)</span>
        </div>
      </div>

      <div className="absolute top-3 left-3 pointer-events-none select-none text-white/40 text-[10px] tracking-wider uppercase font-bold">
        3D Nebula Molecular Cloud Collapse
      </div>
    </div>
  );
}
